import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AnimationService } from '../../services/animation.service';
import { WebsocketService } from '../../services/websocket.service';
import { Output } from '../../model/io-config';
import { TranslatePipe } from '@ngx-translate/core';

type NumberField = 'on' | 'off' | 'limit';

@Component({
  selector: 'app-output-edit',
  templateUrl: './output-edit.component.html',
  styleUrls: ['./output-edit.component.scss'],
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule]
})
export class OutputEditComponent implements OnInit {
  output: Output = {
    uuid: "",
    id: 0,
    name: '',
    type: 'Simple',
    alice: false,
    default: 'off',
    state: 'off'
  };  
  // Labels are translation keys; the template pipes them so a language switch repaints them.
  outputTypes = [
    { value: 'Simple', label: 'output.type.Simple' },
    { value: 'Timed', label: 'output.type.Timed' }
  ];
  defaultValues = [
    { value: 'on', label: 'common.on' },
    { value: 'off', label: 'common.off' }
  ];
  formError: string = '';
  
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private animationService: AnimationService,
    private websocketService: WebsocketService
  ) {
  }

  public ngOnInit(): void {
    const eid = this.route.snapshot.paramMap.get('eid');    
    // Get output data from route state
    if (eid) {
      this.websocketService.getUserDevices().subscribe((devices) => {
        if (devices == null)
          return;        
        this.output = devices.reduce((acc: any, c: { config: { outputs: any[] } }) => {
            if (acc) return acc;
            return c?.config.outputs?.find((o: any) => o.eid === eid) || null;
          }, null as any);            
      });
    }
  }

  public onTypeChange(): void {
    // Reset timer-specific fields when type changes
    if (this.output.type !== 'Timed') {
      this.output.on = undefined;
      this.output.off = undefined;
    }
    // Reset limit when type is not 's'
    if (this.output.type !== 'Simple') {
      this.output.limit = undefined;
    }
  }

  public onAliceChange(): void {
    // Reset room when alice is false
    if (!this.output.alice) {
      this.output.room = undefined;
    }
  }

  public onNotifyChange(id: number): void {
    // Reset room when notify is false
    if (this.output.notify) {
      if (!this.output.notifyOn) {
        this.output.notifyOn = id + ' switched on';
      }
      if (!this.output.notifyOff) {
        this.output.notifyOff = id + ' switched off';
      }
    } else {
      this.output.notifyOn = undefined;
      this.output.notifyOff = undefined;      
    }
  }

  public incrementValue(field: NumberField): void {
    const currentValue = this.output[field] ?? 0;
    this.output[field] = currentValue + 1;
  }

  public decrementValue(field: NumberField): void {
    const currentValue = this.output[field] ?? 0;
    if (currentValue > 0) {
      this.output[field] = currentValue - 1;
    }
  }

  public save(): void {    
    this.formError = '';
    
    if (this.output.name == null || this.output.name.trim() === '') {
      this.formError = 'output.edit.err.name_required';
      return;
    }
    // Validate room name when Alice is enabled
    if (this.output.alice && (!this.output.room || this.output.room.trim() === '')) {
      this.formError = 'output.edit.err.room_required';
      return;
    }

    if ((this.output.type === 'Timed') && 
       (this.output.on == null || this.output.off == null ||
        this.output.on <= 0 || this.output.off <= 0)) {
      this.formError = 'output.edit.err.on_off_required';
      return;
    }
console.log('Saving output: ', this.output);
    this.websocketService.sendMessage({
        type: 'UPDATEOUTPUT',
        payload: this.output,
      });
    this.back();
  }

  public back(): void {
    this.animationService.triggerLeaveAnimation();
    setTimeout(() => {
      const fromTab = (window.history && (window.history.state as any)?.fromTab) ? (window.history.state as any).fromTab : undefined;
      const mac = this.output.mac || (window.history && (window.history.state as any)?.controllerMac);
      if (mac) {
        if (fromTab) {
          this.router.navigate(['/controller', mac], { state: { activeTab: fromTab } });
        } else {
          this.router.navigate(['/controller', mac]);
        }
      } else {
        this.location.back();
      }
    }, 150); // Half the animation duration for smoother transition
  }  
}
